import { __decorate } from "tslib";
// Core
import { Component, Mixins, Prop, Watch, Inject } from 'vue-property-decorator';
import { required, maxLength } from 'vuelidate/lib/validators';
// Extends
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
import { validationMixin } from 'vuelidate';
import FormValidationsMixin from '@/helpers/vue_mixins/v2/FormValidationsMixin';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import CategoryTreeMixin from '@/helpers/vue_mixins/v3/CategoryTreeMixin';
import PromptUpdaterMixin from '@/helpers/vue_mixins/v3/PromptUpdaterMixin';
// Children
import ActionButton from '@/components/action_button/Index.vue';
import BaseImagePlaceholder from '@/components/v2/BaseImagePlacelholder';
import BaseTextArea from '@/components/v2/BaseTextArea';
import CategoryPicker from '@/components/v3/CategoryPicker.vue';
import FacebookPostPicker from '@/components/v3/modals/FacebookPostPicker.vue';
import FacebookPostThumb from '@/components/v3/FacebookPostThumb.vue';
import Modal from '@/components/v3/Modal.vue';
import MoreInfo from '@/components/v3/MoreInfo.vue';
import SwitchTransition from '@/components/page_transition/Switch.vue';
import Uploader from '@/components/v2/Uploader';
// Models / Helpers
import { CategoryTree } from '@/helpers/constants/CategoryTree';
import { maxLength as maxLengthMessage } from '@/helpers/mixins/LocalizationHelper';
import PromptContextQuery from '@/plugins/graphql/queries/v3/fetch-prompt-context.gql';
import PromptGenerateDescription from '@/plugins/graphql/mutations/v3/prompts/generate-product-description.gql';
let ProductDescriptionExplainer = class ProductDescriptionExplainer extends Mixins(Mixins(AlertMixin, CleverTapEventsMixin, FormValidationsMixin, validationMixin, ViewportSizeMixin), CategoryTreeMixin, PromptUpdaterMixin) {
    constructor() {
        super(...arguments);
        this.categoryOptions = CategoryTree;
        this.targetAudienceOptions = [
            {
                value: 'ageRange',
                text: 'Age Range',
            },
            {
                value: 'gender',
                text: 'Gender',
            },
            {
                value: 'familyStatus',
                text: 'Family Status',
            },
            {
                value: 'educationLevel',
                text: 'Education Level',
            },
            {
                value: 'incomeLevel',
                text: 'Income Level',
            },
            {
                value: 'region',
                text: 'Specific Region (Philippine Regions)',
            },
            {
                value: 'interestHobbies',
                text: 'Interest and Hobbies',
            },
            {
                value: 'personality',
                text: 'Personality Traits',
            },
            {
                value: 'occupation',
                text: 'Occupation',
            },
        ];
        this.form = {
            productName: null,
            productImage: null,
            categoryTree: this.baseCategories(),
            businessDescription: null,
            keyFeatures: null,
            targetAudience: this.targetAudienceOptions
                .map((el) => {
                return { [el.value]: null };
            })
                .reduce((a, v) => ({
                ...a,
                ...v,
            }), {}),
            // #CMTGRP: Temporarily disable tone and style
            // tone: [],
            keywords: null,
        };
        this.selectedTargetAudiences = [];
        this.expandedBasicDetails = [false, true];
        this.expandedStoreDetails = [false, true];
        this.expandedAdditionalDetails = [false, true];
        // #CMTGRP: Temporarily disable tone and style
        // private formDataIsLoaded = [false, false];
        this.formDataIsLoaded = false;
        this.isLoadingFormData = true;
        this.hasGenerated = false;
        this.generationInProgress = true;
        this.generatedDescription = null;
        this.generatedReason = null;
        this.promptUUID = '';
    }
    onToggleExplainerChanged() {
        if (this.toggleExplainer) {
            this.generate();
        }
    }
    onFormDataIsLoadedChanged() {
        // #CMTGRP: Temporarily disable tone and style
        // if (this.formDataIsLoaded.every((el) => el)) {
        //   this.isLoadingFormData = false;
        //   this.generate();
        // }
        if (this.formDataIsLoaded) {
            this.isLoadingFormData = false;
            this.generatePrompt();
        }
    }
    get displayGeneratedDescription() {
        if (this.generatedDescription) {
            return this.generatedDescription.replace(/\n/g, '<br>');
        }
        return '';
    }
    get imageURL() {
        if (this.value?.image?.file) {
            return URL.createObjectURL(this.value.image.file);
        }
        return this.value?.image?.url;
    }
    get targetAudienceSummary() {
        if (this.selectedTargetAudiences.length === 0) {
            return 'None';
        }
        return this.selectedTargetAudiences
            .map((key) => {
            return this.targetAudienceLabel(key);
        })
            .join(', ');
    }
    targetAudienceLabel(field) {
        return this.targetAudienceOptions.find((el) => el.value === field).text;
    }
    targetAudienceError(field, value, length) {
        if (value?.length > length) {
            return [maxLengthMessage(field, length)];
        }
    }
    get toneAndStyleSummary() {
        if (this.form.tone.length === 0) {
            return 'None';
        }
        if (this.form.tone.length === 1) {
            return `${this.form.tone.length} post selected`;
        }
        return `${this.form.tone.length} posts selected`;
    }
    deselectPost(post) {
        const index = this.form.tone.findIndex((p) => p.id === post.id);
        if (index !== -1) {
            this.form.tone.splice(index, 1);
        }
    }
    async generatePrompt() {
        this.generateProductDescriptionAction(this.$store.state.appName, 'GENERATE', this.form.productName);
        await this.generate();
    }
    async acceptPrompt() {
        const payload = {
            name: this.form.productName,
            description: this.generatedDescription,
        };
        await this.updateAction(this.promptUUID, 'accept');
        this.generateProductDescriptionAction(this.$store.state.appName, 'ACCEPT', this.form.productName);
        this.$emit('onAccept', payload);
    }
    async regeneratePrompt() {
        await this.updateAction(this.promptUUID, 'regenerate');
        this.generateProductDescriptionAction(this.$store.state.appName, 'REGENERATE', this.form.productName);
        this.generatedDescription = null;
        this.generatedReason = null;
        await this.generate();
    }
    ignorePrompt() {
        this.generateProductDescriptionAction(this.$store.state.appName, 'IGNORE', this.form.productName);
        this.$emit('onBack');
    }
    generate() {
        this.$v.$touch();
        if (this.$v.$invalid) {
            return;
        }
        this.generationInProgress = true;
        return this.$apollo
            .mutate({
            mutation: PromptGenerateDescription,
            variables: {
                input: {
                    appUuid: this.$store.state.appUUID,
                    productName: this.form.productName,
                    productUuid: this.productUUID,
                    image: [this.value.image.file],
                    galleryPhotoUuid: this.value.image.uuid,
                    primaryCategories: Object.keys(this.form.categoryTree).filter((key) => this.form.categoryTree[key].primaryOption),
                    secondaryCategories: Object.keys(this.form.categoryTree)
                        .map((key) => this.form.categoryTree[key].secondaryOptions)
                        .flat(),
                    businessDescription: this.form.businessDescription,
                    promptContext: {
                        keyFeatures: this.form.keyFeatures,
                        // #CMTGRP: Temporarily disable tone and style
                        // fbPostReferences: this.form.tone.map((post) => {
                        //   return post.id;
                        // }),
                        targetAudience: this.form.targetAudience,
                        keywords: this.form.keywords,
                    },
                },
            },
        })
            .then((result) => {
            this.hasGenerated = true;
            this.generationInProgress = false;
            this.generatedDescription =
                result.data.generateProductDescription.prompt.jsonCompletion.answer;
            this.generatedReason = result.data.generateProductDescription.prompt.jsonCompletion.reason;
            this.promptUUID = result.data.generateProductDescription.prompt.base.uuid;
            this.$emit('onGenerate', result.data.generateProductDescription.prompt.uuid);
        })
            .catch((e) => {
            this.generationInProgress = false;
            this.errorNotification(e.message);
        });
    }
    mounted() {
        this.form.productName = this.value.productName;
        this.form.productImage = this.value.productImage;
        this.generatedDescription = this.value.productDescription;
    }
};
__decorate([
    Prop({ type: Object, default: null })
], ProductDescriptionExplainer.prototype, "value", void 0);
__decorate([
    Prop({ type: Boolean, default: null })
], ProductDescriptionExplainer.prototype, "toggleExplainer", void 0);
__decorate([
    Watch('toggleExplainer')
], ProductDescriptionExplainer.prototype, "onToggleExplainerChanged", null);
__decorate([
    Inject({ default: null })
], ProductDescriptionExplainer.prototype, "productUUID", void 0);
__decorate([
    Watch('formDataIsLoaded')
], ProductDescriptionExplainer.prototype, "onFormDataIsLoadedChanged", null);
ProductDescriptionExplainer = __decorate([
    Component({
        components: {
            ActionButton,
            BaseImagePlaceholder,
            BaseTextArea,
            CategoryPicker,
            FacebookPostPicker,
            FacebookPostThumb,
            Modal,
            MoreInfo,
            SwitchTransition,
            Uploader,
        },
        apollo: {
            // #CMTGRP: Temporarily disable tone and style
            // topFacebookPosts: {
            //   query: FacebookPostsQuery,
            //   variables() {
            //     return {
            //       appUuid: this.$store.state.appUUID,
            //     };
            //   },
            //   update({ fetchPagePosts }) {
            //     if (fetchPagePosts) {
            //       this.form.tone = fetchPagePosts.data;
            //       // Because @Watch deep: true doesn't seem to work for direct assignment
            //       this.formDataIsLoaded = [true, this.formDataIsLoaded[1]];
            //     }
            //   },
            //   skip() {
            //     return !this.$store.state.appUUID;
            //   },
            // },
            promptContext: {
                query: PromptContextQuery,
                variables() {
                    return {
                        appUuid: this.$store.state.appUUID,
                    };
                },
                update({ app }) {
                    this.form.categoryTree = this.buildCategoryTree(app.categoryTree);
                    this.form.businessDescription = app.businessDescription;
                    // Because @Watch deep: true doesn't seem to work for direct assignment
                    // this.formDataIsLoaded = [this.formDataIsLoaded[0], true];
                    // #CMTGRP: Temporarily disable tone and style
                    this.formDataIsLoaded = true;
                },
                skip() {
                    return !this.$store.state.appUUID;
                },
            },
        },
        validations: {
            form: {
                // Fields with required() returning true is to utilize FormValidationMixin without error
                productName: {
                    required,
                    maxLength: maxLength(50),
                },
                // productImage: {
                //   required,
                // },
                categoryTree: {
                    required() {
                        const { categoryTree } = this.form;
                        const keys = Object.keys(categoryTree);
                        return keys.filter((key) => categoryTree[key].primaryOption === true).length > 0;
                    },
                },
                businessDescription: {
                    required,
                    maxLength: maxLength(800),
                },
                keyFeatures: {
                    required: () => {
                        return true;
                    },
                    maxLength: maxLength(50),
                },
                targetAudience: {
                // required,
                },
                tone: {
                    maxLength: maxLength(3),
                },
                keywords: {
                    required: () => {
                        return true;
                    },
                    maxLength: maxLength(50),
                },
            },
        },
    })
], ProductDescriptionExplainer);
export default ProductDescriptionExplainer;
